/**
 * Button Component — Apex Design System v3.
 *
 * Five weight levels: primary, secondary, ghost, danger, gradient.
 * Three sizes: sm (34px), md (38px), lg (46px).
 *
 * Premium details:
 * - Primary: gradient with inner light edge, shadow lift on hover
 * - Gradient: animated gradient background with glow on hover
 * - Secondary: refined border with surface tint on hover
 * - All: spring-based active press (scale 0.97), focus glow ring
 * - Icon glow on hover for primary/gradient variants
 */

import { clsx } from "clsx";
import type { ButtonHTMLAttributes } from "react";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "danger" | "gradient";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
}

const variantStyles = {
  primary:
    "bg-gradient-to-b from-[var(--color-interactive-primary)] to-[var(--color-interactive-primary-hover)] text-[var(--text-inverse)] " +
    "shadow-[var(--shadow-sm),inset_0_1px_0_oklch(100%_0_0/0.15)] " +
    "hover:shadow-[var(--shadow-md),inset_0_1px_0_oklch(100%_0_0/0.20)] hover:-translate-y-px " +
    "active:scale-[0.97] active:shadow-[var(--shadow-xs)] active:translate-y-0 " +
    "focus-visible:shadow-[var(--focus-ring)] icon-glow-hover",
  gradient:
    "text-[var(--text-inverse)] " +
    "shadow-[var(--shadow-sm),inset_0_1px_0_oklch(100%_0_0/0.15)] " +
    "hover:shadow-[var(--shadow-lg),0_0_20px_oklch(62%_0.22_285/0.15)] hover:-translate-y-px " +
    "active:scale-[0.97] active:shadow-[var(--shadow-xs)] active:translate-y-0 " +
    "focus-visible:shadow-[var(--focus-ring)] icon-glow-hover",
  secondary:
    "bg-[var(--surface-base)] text-[var(--text-primary)] " +
    "border border-[var(--border-default)] " +
    "shadow-[var(--shadow-xs),var(--inner-glow-subtle)] " +
    "hover:bg-[var(--surface-raised)] hover:border-[var(--border-strong)] hover:shadow-[var(--shadow-sm)] " +
    "active:scale-[0.97] active:shadow-none " +
    "focus-visible:shadow-[var(--focus-ring)]",
  ghost:
    "bg-transparent text-[var(--color-interactive-primary)] " +
    "hover:bg-[var(--color-interactive-primary)]/8 " +
    "active:scale-[0.97] active:bg-[var(--color-interactive-primary)]/12 " +
    "focus-visible:shadow-[var(--focus-ring)]",
  danger:
    "bg-[var(--fill-danger)] text-[var(--text-danger)] " +
    "border border-[var(--text-danger)]/20 " +
    "shadow-[var(--shadow-xs)] " +
    "hover:bg-[var(--text-danger)]/15 hover:shadow-[var(--shadow-sm)] " +
    "active:scale-[0.97] active:shadow-none " +
    "focus-visible:shadow-[var(--focus-ring)]",
};

const sizeStyles = {
  sm: "h-[34px] px-3.5 text-xs gap-1.5 rounded-[var(--radius-md)]",
  md: "h-[38px] px-4 text-[13px] gap-2 rounded-[var(--radius-md)]",
  lg: "h-[46px] px-6 text-sm gap-2.5 rounded-[var(--radius-lg)]",
};

function Button({
  variant = "primary",
  size = "md",
  isLoading = false,
  disabled,
  className,
  children,
  style,
  ...props
}: ButtonProps) {
  const gradientStyle =
    variant === "gradient"
      ? {
          background:
            "linear-gradient(135deg, oklch(55% 0.22 285), oklch(55% 0.20 320), oklch(55% 0.22 285))",
          backgroundSize: "200% 200%",
          animation: "gradientShift 4s ease infinite",
          ...style,
        }
      : style;

  return (
    <button
      className={clsx(
        "inline-flex items-center justify-center font-semibold",
        "transition-all duration-200 ease-[var(--ease-spring)]",
        "focus:outline-none",
        "disabled:pointer-events-none disabled:opacity-40",
        "select-none",
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      disabled={disabled || isLoading}
      style={gradientStyle}
      {...props}
    >
      {isLoading && (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      )}
      {children}
    </button>
  );
}

export default Button;
